'use client';

import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Save, X, Pencil, Package } from 'lucide-react';
import { toast } from 'sonner';
import type { MediaItem } from '@/lib/types';

const CUSTOM_ITEMS_KEY = 'mediadispatch_custom_items';

function loadCustomItems(): MediaItem[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = localStorage.getItem(CUSTOM_ITEMS_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function saveCustomItems(items: MediaItem[]) {
  if (typeof window === 'undefined') return;
  localStorage.setItem(CUSTOM_ITEMS_KEY, JSON.stringify(items));
}

interface CustomItemFormState {
  title: string;
  year: string;
  status: string;
  genres: string;
  rating: string;
  overview: string;
  posterUrl: string;
  customFieldsRaw: { key: string; value: string }[];
}

const emptyForm = (): CustomItemFormState => ({
  title: '',
  year: String(new Date().getFullYear()),
  status: 'Available',
  genres: '',
  rating: '7.0',
  overview: '',
  posterUrl: '',
  customFieldsRaw: [],
});

interface ItemCardProps {
  item: MediaItem;
  onEdit: (item: MediaItem) => void;
  onDelete: (id: string) => void;
}

function ItemCard({ item, onEdit, onDelete }: ItemCardProps) {
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  return (
    <div className="card-elevated flex items-center gap-3 p-3">
      <div className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
        <Package size={14} className="text-muted-foreground" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-foreground truncate">{item.title}</p>
        <p className="text-2xs text-muted-foreground">
          {item.year} · {item.status}
          {item.genres.length > 0 && ` · ${item.genres.slice(0, 2).join(', ')}`}
        </p>
      </div>
      <div className="flex items-center gap-1 flex-shrink-0">
        <button
          type="button"
          onClick={() => onEdit(item)}
          className="btn-ghost p-2 rounded-lg"
          title="Edit item"
        >
          <Pencil size={13} />
        </button>
        {deleteConfirm ? (
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => onDelete(item.id)}
              className="px-2 py-1 text-2xs font-semibold rounded-md bg-danger text-white"
            >
              Delete
            </button>
            <button
              type="button"
              onClick={() => setDeleteConfirm(false)}
              className="btn-ghost px-2 py-1 text-2xs"
            >
              Cancel
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setDeleteConfirm(true)}
            className="btn-ghost p-2 rounded-lg text-muted-foreground hover:text-danger"
          >
            <Trash2 size={13} />
          </button>
        )}
      </div>
    </div>
  );
}

interface ItemFormProps {
  initial?: MediaItem | null;
  onSave: (item: MediaItem) => void;
  onCancel: () => void;
}

function ItemForm({ initial, onSave, onCancel }: ItemFormProps) {
  const [form, setForm] = useState<CustomItemFormState>(() => {
    if (initial) {
      return {
        title: initial.title,
        year: String(initial.year),
        status: initial.status,
        genres: initial.genres.join(', '),
        rating: String(initial.rating),
        overview: initial.overview ?? '',
        posterUrl: initial.posterUrl,
        customFieldsRaw: Object.entries(initial.customFields ?? {}).map(([key, value]) => ({ key, value })),
      };
    }
    return emptyForm();
  });

  const addCustomField = () => {
    setForm((f) => ({ ...f, customFieldsRaw: [...f.customFieldsRaw, { key: '', value: '' }] }));
  };

  const updateCustomField = (idx: number, patch: Partial<{ key: string; value: string }>) => {
    setForm((f) => ({
      ...f,
      customFieldsRaw: f.customFieldsRaw.map((cf, i) => (i === idx ? { ...cf, ...patch } : cf)),
    }));
  };

  const removeCustomField = (idx: number) => {
    setForm((f) => ({ ...f, customFieldsRaw: f.customFieldsRaw.filter((_, i) => i !== idx) }));
  };

  const handleSave = () => {
    if (!form.title.trim()) {
      toast.error('Title is required');
      return;
    }
    const customFields: Record<string, string> = {};
    for (const cf of form.customFieldsRaw) {
      if (cf.key.trim()) customFields[cf.key.trim()] = cf.value;
    }
    const item: MediaItem = {
      id: initial?.id ?? `custom-${Date.now()}`,
      title: form.title.trim(),
      year: parseInt(form.year) || new Date().getFullYear(),
      status: form.status.trim() || 'Available',
      genres: form.genres.split(',').map((g) => g.trim()).filter(Boolean),
      rating: parseFloat(form.rating) || 0,
      posterUrl: form.posterUrl.trim(),
      mediaType: 'custom',
      overview: form.overview.trim() || undefined,
      monitored: true,
      isCustom: true,
      customFields,
      sourceInstanceName: 'Custom',
    };
    onSave(item);
  };

  return (
    <div className="card-elevated p-5 flex flex-col gap-4 fade-in">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-foreground">{initial ? 'Edit Item' : 'New Custom Item'}</h3>
        <button type="button" onClick={onCancel} className="btn-ghost p-1 rounded-lg">
          <X size={14} />
        </button>
      </div>

      {/* Title */}
      <div>
        <label className="block text-xs font-medium text-muted-foreground mb-1">Title <span className="text-danger">*</span></label>
        <input
          type="text"
          value={form.title}
          onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
          placeholder="e.g. My Custom Show"
          className="input-base w-full px-3 py-2 text-sm"
        />
      </div>

      <div className="grid grid-cols-3 gap-3">
        {/* Year */}
        <div>
          <label className="block text-xs font-medium text-muted-foreground mb-1">Year</label>
          <input
            type="number"
            value={form.year}
            onChange={(e) => setForm((f) => ({ ...f, year: e.target.value }))}
            className="input-base w-full px-3 py-2 text-sm"
          />
        </div>
        {/* Status */}
        <div>
          <label className="block text-xs font-medium text-muted-foreground mb-1">Status</label>
          <input
            type="text"
            value={form.status}
            onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
            placeholder="Available"
            className="input-base w-full px-3 py-2 text-sm"
          />
        </div>
        {/* Rating */}
        <div>
          <label className="block text-xs font-medium text-muted-foreground mb-1">Rating</label>
          <input
            type="number"
            step="0.1"
            min="0"
            max="10"
            value={form.rating}
            onChange={(e) => setForm((f) => ({ ...f, rating: e.target.value }))}
            className="input-base w-full px-3 py-2 text-sm"
          />
        </div>
      </div>

      {/* Genres */}
      <div>
        <label className="block text-xs font-medium text-muted-foreground mb-1">Genres</label>
        <input
          type="text"
          value={form.genres}
          onChange={(e) => setForm((f) => ({ ...f, genres: e.target.value }))}
          placeholder="Drama, Thriller (comma-separated)"
          className="input-base w-full px-3 py-2 text-sm"
        />
      </div>

      {/* Poster URL */}
      <div>
        <label className="block text-xs font-medium text-muted-foreground mb-1">Poster URL</label>
        <input
          type="url"
          value={form.posterUrl}
          onChange={(e) => setForm((f) => ({ ...f, posterUrl: e.target.value }))}
          placeholder="https://..."
          className="input-base w-full px-3 py-2 text-sm font-mono-data"
        />
      </div>

      {/* Overview */}
      <div>
        <label className="block text-xs font-medium text-muted-foreground mb-1">Overview</label>
        <textarea
          value={form.overview}
          onChange={(e) => setForm((f) => ({ ...f, overview: e.target.value }))}
          placeholder="Short description..."
          rows={2}
          className="input-base w-full px-3 py-2 text-sm resize-none"
        />
      </div>

      {/* Custom fields */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-xs font-medium text-muted-foreground">Custom Payload Fields</label>
          <button
            type="button"
            onClick={addCustomField}
            className="flex items-center gap-1 text-2xs font-medium btn-ghost px-2 py-1 rounded-md border border-border"
          >
            <Plus size={11} />
            Add Field
          </button>
        </div>
        {form.customFieldsRaw.length === 0 ? (
          <p className="text-2xs text-muted-foreground">No custom fields. These are merged into the payload item.</p>
        ) : (
          <div className="flex flex-col gap-2">
            {form.customFieldsRaw.map((cf, idx) => (
              <div key={`cf-${idx}`} className="flex items-center gap-2">
                <input
                  type="text"
                  value={cf.key}
                  onChange={(e) => updateCustomField(idx, { key: e.target.value })}
                  placeholder="key"
                  className="input-base px-2 py-1 text-2xs font-mono-data w-28 flex-shrink-0"
                />
                <span className="text-2xs text-muted-foreground">:</span>
                <input
                  type="text"
                  value={cf.value}
                  onChange={(e) => updateCustomField(idx, { value: e.target.value })}
                  placeholder="value"
                  className="input-base px-2 py-1 text-2xs font-mono-data flex-1"
                />
                <button
                  type="button"
                  onClick={() => removeCustomField(idx)}
                  className="text-muted-foreground hover:text-danger flex-shrink-0"
                >
                  <Trash2 size={12} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="flex items-center gap-2 pt-1">
        <button type="button" onClick={onCancel} className="px-4 py-2 text-sm font-medium btn-ghost">
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSave}
          className="flex items-center gap-2 px-5 py-2 text-sm font-semibold btn-primary"
        >
          <Save size={14} />
          {initial ? 'Update Item' : 'Add Item'}
        </button>
      </div>
    </div>
  );
}

export default function CustomItemsPanel() {
  const [items, setItems] = useState<MediaItem[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState<MediaItem | null>(null);

  useEffect(() => {
    setItems(loadCustomItems());
  }, []);

  const handleSave = (item: MediaItem) => {
    let updated: MediaItem[];
    if (editingItem) {
      updated = items.map((i) => (i.id === item.id ? item : i));
      toast.success(`"${item.title}" updated`);
    } else {
      updated = [...items, item];
      toast.success(`"${item.title}" added`);
    }
    setItems(updated);
    saveCustomItems(updated);
    setShowForm(false);
    setEditingItem(null);
  };

  const handleEdit = (item: MediaItem) => {
    setEditingItem(item);
    setShowForm(true);
  };

  const handleDelete = (id: string) => {
    let updated = items.filter((i) => i.id !== id);
    setItems(updated);
    saveCustomItems(updated);
    toast.success('Item removed');
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingItem(null);
  };

  return (
    <div className="flex flex-col gap-6">
      {/* Panel header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-muted border border-border">
            <Package size={18} className="text-muted-foreground" />
          </div>
          <div>
            <h2 className="text-base font-semibold text-foreground">Custom Items</h2>
            <p className="text-xs text-muted-foreground">
              {items.length} custom item{items.length !== 1 ? 's' : ''} configured
            </p>
          </div>
        </div>
        {!showForm && (
          <button
            type="button"
            onClick={() => { setEditingItem(null); setShowForm(true); }}
            className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium btn-primary"
          >
            <Plus size={14} />
            Add Item
          </button>
        )}
      </div>

      {/* Form */}
      {showForm && (
        <ItemForm initial={editingItem} onSave={handleSave} onCancel={handleCancel} />
      )}

      {/* Items list */}
      {!showForm && items.length === 0 ? (
        <div className="card-elevated flex flex-col items-center justify-center py-14 text-center">
          <div className="w-12 h-12 rounded-xl bg-muted flex items-center justify-center mb-3">
            <Package size={22} className="text-muted-foreground" />
          </div>
          <h3 className="text-sm font-semibold text-foreground mb-1">No custom items</h3>
          <p className="text-xs text-muted-foreground max-w-xs">
            Add custom items to include alongside Sonarr series and Radarr movies in the media library.
          </p>
        </div>
      ) : (
        !showForm && (
          <div className="flex flex-col gap-3">
            {items.map((item) => (
              <ItemCard key={item.id} item={item} onEdit={handleEdit} onDelete={handleDelete} />
            ))}
          </div>
        )
      )}

      {!showForm && (
        <div className="p-3 rounded-lg connection-idle flex items-start gap-2">
          <Package size={13} className="flex-shrink-0 mt-0.5" />
          <p className="text-2xs">
            Custom items appear in the media library alongside Sonarr and Radarr content. Custom payload fields are merged into the dispatch payload.
          </p>
        </div>
      )}
    </div>
  );
}

export { loadCustomItems };
